/* eslint-disable array-callback-return */
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Button, Dropdown, Menu, Icon, Empty, Tooltip } from 'antd'
import styled from 'styled-components'
import { partial } from 'lodash'
import {
  fadedGrey,
  lightGreySecondary,
  themeColor,
  themeColorBlue,
  white,
} from '@edulastic/colors'

import { useInternalEffect } from '../../hooks/useInternalEffect'

const CustomMenu = (
  className,
  data,
  dataCy,
  handleMenuClick,
  prefix,
  selected,
  focusedIndex,
  setFocusedIndex,
  menuRef
) => (
  <Menu
    selectedKeys={[selected.key]}
    className={`${className}`}
    onClick={handleMenuClick}
    ref={menuRef}
    role="menu"
  >
    <Menu.Item key="0" disabled>
      {data.length ? (
        prefix
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ textAlign: 'left', margin: '10px 0' }}
        />
      )}
    </Menu.Item>
    {data.map((item, idx) => (
      <Menu.Item
        key={item.key}
        title={item.title}
        id={`${dataCy}-Option-${idx}`}
        disabled={item.disabled}
        className={focusedIndex === idx ? 'ant-dropdown-menu-item-active' : ''}
        style={
          focusedIndex === idx
            ? { background: themeColorBlue, color: white }
            : {}
        }
        role="menuitem"
        aria-label={item.title}
      >
        {item.tooltip ? (
          <Tooltip title={item.tooltip} placement="right">
            <TooltipTrigger>{item.title}</TooltipTrigger>
          </Tooltip>
        ) : (
          item.title
        )}
      </Menu.Item>
    ))}
  </Menu>
)

function searchItemOrDefault(data, searchItem) {
  let item = null
  if (data.length) {
    item = data.find((_item) => {
      if (typeof searchItem === 'string' && _item.key === searchItem) {
        return true
      }
      if (
        searchItem &&
        typeof searchItem === 'object' &&
        _item.key === searchItem.key
      ) {
        return true
      }
    })
    if (!item || item.disabled) {
      item = data.find((option) => !option.disabled) || data[0]
    }
  } else {
    item = { key: '', title: '' }
  }
  return item
}

const ControlDropDown = ({
  className,
  dataCy = '',
  containerClassName = '',
  prefix = '',
  showPrefixOnSelected = true,
  by,
  selectCB,
  data,
  comData,
  trigger = ['click'],
  buttonWidth,
  style,
  height = '32px',
  placeholder = '',
  overlayStyle,
  getPopupContainer,
  buttonAriaLabel = `${prefix} dropdown`,
  onDisabledItemClick,
  ...props
}) => {
  const [selected, setSelected] = useState(by)
  const [isActive, setActive] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const menuRef = useRef(null)

  // FIXME: WHY Internal Effect ??
  // (setState synchronously in render fn is wrong.)
  // Moreover, setState is asynchronous, so it'll take effect in next render anyways.
  useInternalEffect(() => {
    // FIXME: `selected` should be `by`. Otherwise, when both `by` and `data` change, `selected` will be `data[0]` instead of `by`.
    const item = searchItemOrDefault(data, selected)
    setSelected(item)
  }, [data])

  useInternalEffect(() => {
    const item = searchItemOrDefault(data, by)
    setSelected(item)
  }, [by])

  const handleMenuClick = useCallback(
    (event) => {
      const item = data.find((option) => option.key === event.key)
      if (item?.disabled) {
        setActive(false)
        if (onDisabledItemClick) {
          onDisabledItemClick(item, comData)
        }
        return
      }
      const _selected = {
        key: event.key,
        title: event.item.props.title || item?.title,
      }
      setActive(false)
      setSelected(_selected)
      if (selectCB) {
        selectCB(event, _selected, comData)
      }
    },
    [selectCB, data, onDisabledItemClick, comData]
  )

  // Reset focus index when menu opens
  useEffect(() => {
    if (isActive) {
      setFocusedIndex(data.findIndex((item) => item.key === selected.key) || 0)
    }
  }, [isActive, data, selected])

  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!isActive) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1 >= data.length ? 0 : prev + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev - 1 < 0 ? data.length - 1 : prev - 1))
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const item = data[focusedIndex]
      if (!item) return
      if (item.disabled) {
        setActive(false)
        if (onDisabledItemClick) {
          onDisabledItemClick(item, comData)
        }
        return
      }
      handleMenuClick({
        key: item.key,
        item: { props: { title: item.title } },
      })
    } else if (e.key === 'Escape') {
      setActive(false)
    }
  }

  const title = (selected && selected.title) || prefix

  return (
    <StyledDiv
      className={`${containerClassName} control-dropdown`}
      data-cy={dataCy}
      buttonWidth={buttonWidth}
      style={style}
      height={height}
      {...props}
    >
      <Dropdown
        onVisibleChange={setActive}
        overlay={partial(
          CustomMenu,
          className,
          data,
          dataCy,
          handleMenuClick,
          prefix,
          selected,
          focusedIndex,
          setFocusedIndex,
          menuRef
        )}
        trigger={trigger}
        overlayStyle={overlayStyle}
        getPopupContainer={getPopupContainer}
        labelledBy="sdfdsfsdf"
        id="testId"
        visible={isActive}
      >
        <Button
          aria-label={buttonAriaLabel}
          title={title}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          onClick={() => setActive(!isActive)}
          aria-haspopup="menu"
          aria-expanded={isActive}
          aria-activedescendant={`${dataCy}-Option-${focusedIndex}`}
        >
          {(showPrefixOnSelected ? `${prefix} ` : '') + selected?.title ||
            placeholder}
          <Icon type={isActive ? 'up' : 'down'} />
        </Button>
      </Dropdown>
    </StyledDiv>
  )
}

// Matches `.ant-dropdown-menu-item { padding: 4px 12px }` so the tooltip
// trigger covers the full option row, not only the label text.
const TooltipTrigger = styled.span`
  pointer-events: auto;
  display: block;
  margin: -4px -12px;
  padding: 4px 12px;
`

const StyledDiv = styled.div`
  button {
    height: ${(props) => props.height || 'auto'};
    display: flex;
    justify-content: start;
    align-items: center;
    font-size: 11px;
    width: ${({ buttonWidth }) => buttonWidth || 'auto'};
    &.ant-btn.ant-dropdown-trigger {
      background-color: ${lightGreySecondary};
      border-color: ${fadedGrey};

      &.ant-dropdown-open {
        background-color: transparent;
      }

      &:hover,
      &:focus {
        border-color: ${themeColorBlue};
        color: ${themeColor};
      }
      i {
        color: ${themeColor};
      }
      .anticon {
        height: 13px;
        font-size: 11px;
        transform: none;
      }
    }

    span {
      flex: 1;
      text-align: left;
      overflow: hidden;
    }
  }
`

/** @type {typeof ControlDropDown} */
const StyledControlDropDown = styled(ControlDropDown)`
  max-height: 250px;
  overflow: auto;
  .ant-dropdown-menu-item {
    padding: 4px 12px;
  }

  .ant-dropdown-menu-item-selected,
  .ant-dropdown-menu-item-active {
    background-color: ${themeColorBlue};
    color: ${white};
  }

  .ant-dropdown-menu-item-disabled {
    background-color: ${white};
  }

  .ant-dropdown-menu-item,
  .ant-dropdown-menu-submenu-title {
    font-size: 11px;
  }
`

export { StyledControlDropDown as ControlDropDown }
